/**
 * AYU Schlaftracker — Tägliche Schlafeinträge
 * Design nach MENTO-Vorbild: Emoji-basierte Qualität, Zeiten, Erholung, Statistiken
 */
import { useState, useMemo } from "react";
import {
  Moon,
  Sun,
  Clock,
  Zap,
  Battery,
  BatteryLow,
  BatteryMedium,
  BatteryFull,
  BatteryCharging,
  Minus,
  Plus,
  Sparkles,
  BarChart3,
  Calendar,
  Trash2,
  Loader2,
  Info,
  Star,
  Watch,
  Smartphone,
} from "lucide-react";
import { trpc } from "@/lib/trpc";
import { useClientAuth } from "@/contexts/ClientAuthContext";
import { toast } from "sonner";
import { useI18n } from "@/contexts/I18nContext";

/* ═══════════════════════════════════════════════════════════
   Konstanten & Typen
   ═══════════════════════════════════════════════════════════ */

const QUALITY_OPTIONS = [
  { value: 1, emoji: "😫", labelKey: "sehrSchlecht" as const },
  { value: 2, emoji: "😕", labelKey: "schlecht" as const },
  { value: 3, emoji: "😐", labelKey: "okay" as const },
  { value: 4, emoji: "🙂", labelKey: "gut" as const },
  { value: 5, emoji: "🤩", labelKey: "sehrGut" as const },
];

const RECOVERY_OPTIONS = [
  { value: 1, emoji: "🪫", labelKey: "erschoepft" as const },
  { value: 2, emoji: "😩", labelKey: "muede" as const },
  { value: 3, emoji: "😐", labelKey: "okay" as const },
  { value: 4, emoji: "⚡", labelKey: "erholt" as const },
  { value: 5, emoji: "🔋", labelKey: "vollerEnergie" as const },
];

const FALL_ASLEEP_OPTIONS = [
  { value: 1, labelKey: "sofort" as const },
  { value: 2, labelKey: "schnell" as const },
  { value: 3, labelKey: "normal" as const },
  { value: 4, labelKey: "schwer" as const },
  { value: 5, labelKey: "sehrSchwer" as const },
];

function getTodayDate(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}

function formatDateDisplay(dateStr: string, todayStr: string, yesterdayStr: string): string {
  const d = new Date(dateStr + "T12:00:00");
  const today = getTodayDate();
  if (dateStr === today) return todayStr;
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yStr = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, "0")}-${String(yesterday.getDate()).padStart(2, "0")}`;
  if (dateStr === yStr) return yesterdayStr;
  return d.toLocaleDateString("de-DE", { weekday: "short", day: "numeric", month: "short" });
}

function calcSleepDuration(bedtime: string, wakeTime: string): string {
  const [bH, bM] = bedtime.split(":").map(Number);
  const [wH, wM] = wakeTime.split(":").map(Number);
  let totalMin = (wH * 60 + wM) - (bH * 60 + bM);
  if (totalMin < 0) totalMin += 24 * 60;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
}

/* ═══════════════════════════════════════════════════════════
   Hauptkomponente
   ═══════════════════════════════════════════════════════════ */
export default function AppSchlaf() {
  const { t } = useI18n();
  const { client } = useClientAuth();
  const clientId = client?.clientId ?? "";

  const [selectedDate, setSelectedDate] = useState(getTodayDate());
  const [quality, setQuality] = useState(3);
  const [bedtime, setBedtime] = useState("23:00");
  const [wakeTime, setWakeTime] = useState("07:00");
  const [recovery, setRecovery] = useState(3);
  const [fallAsleep, setFallAsleep] = useState(3);
  const [nightWakeups, setNightWakeups] = useState(0);
  const [dreamsRemembered, setDreamsRemembered] = useState(false);
  const [notes, setNotes] = useState("");
  const [hasExisting, setHasExisting] = useState(false);

  // Queries
  const { data: todayEntry, isLoading: loadingToday, refetch: refetchToday } = trpc.sleep.getByDate.useQuery(
    { clientId, date: selectedDate },
    { enabled: !!clientId }
  );

  // Sync form state when entry data changes
  const entryId = todayEntry?.id;
  const entryDate = todayEntry?.date;
  useMemo(() => {
    if (todayEntry) {
      setQuality(todayEntry.quality);
      setBedtime(todayEntry.bedtime);
      setWakeTime(todayEntry.wakeTime);
      setRecovery(todayEntry.recovery);
      setFallAsleep(todayEntry.fallAsleep);
      setNightWakeups(todayEntry.nightWakeups);
      setDreamsRemembered(todayEntry.dreamsRemembered);
      setNotes(todayEntry.notes ?? "");
      setHasExisting(true);
    } else if (todayEntry === null) {
      setQuality(3);
      setBedtime("23:00");
      setWakeTime("07:00");
      setRecovery(3);
      setFallAsleep(3);
      setNightWakeups(0);
      setDreamsRemembered(false);
      setNotes("");
      setHasExisting(false);
    }
  }, [entryId, entryDate, selectedDate]);

  const { data: recentEntries, refetch: refetchRecent } = trpc.sleep.getRecent.useQuery(
    { clientId, limit: 14 },
    { enabled: !!clientId }
  );

  const upsertMutation = trpc.sleep.upsert.useMutation({
    onSuccess: () => {
      toast.success(hasExisting ? t("schlaf.eintraegAktualisiert") : t("schlaf.schlafEintragGespeichert"));
      refetchToday();
      refetchRecent();
    },
    onError: (err) => toast.error(err.message || t("errors.speichern")),
  });

  const deleteMutation = trpc.sleep.delete.useMutation({
    onSuccess: () => {
      toast.success(t("schlaf.eintraegGeloescht"));
      refetchToday();
      refetchRecent();
    },
    onError: (err) => toast.error(err.message || t("errors.speichern")),
  });

  const handleSave = () => {
    if (!clientId) return;
    upsertMutation.mutate({
      clientId,
      date: selectedDate,
      quality,
      bedtime,
      wakeTime,
      recovery,
      fallAsleep,
      nightWakeups,
      dreamsRemembered,
      notes: notes.trim() || undefined,
    });
  };

  const handleDelete = (id: number) => {
    if (!clientId) return;
    deleteMutation.mutate({ id, clientId });
  };

  // Statistiken der letzten 14 Tage
  const stats = useMemo(() => {
    if (!recentEntries || recentEntries.length === 0) return null;
    const avgQuality = recentEntries.reduce((s, e) => s + e.quality, 0) / recentEntries.length;
    const durations = recentEntries.map((e) => {
      const [bH, bM] = e.bedtime.split(":").map(Number);
      const [wH, wM] = e.wakeTime.split(":").map(Number);
      let totalMin = (wH * 60 + wM) - (bH * 60 + bM);
      if (totalMin < 0) totalMin += 24 * 60;
      return totalMin;
    });
    const avgDuration = durations.reduce((s, d) => s + d, 0) / durations.length;
    const avgH = Math.floor(avgDuration / 60);
    const avgM = Math.round(avgDuration % 60);

    const best = recentEntries.reduce((b, e) => (e.quality > b.quality ? e : b), recentEntries[0]);
    const worst = recentEntries.reduce((w, e) => (e.quality < w.quality ? e : w), recentEntries[0]);

    return { avgQuality, avgDurationStr: `${avgH}h ${avgM}min`, best, worst, count: recentEntries.length };
  }, [recentEntries]);

  if (!clientId) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <p className="text-[#2D2D2D]/40 text-sm">{t("schlaf.bitteMeldeAn")}</p>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-[#6b7f4e]/10 flex items-center justify-center">
          <Moon className="w-5 h-5 text-[#6b7f4e]" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-[#2d3a1a]" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("schlaf.title")}
          </h1>
          <p className="text-xs text-[#8a9680]">{t("schlaf.subtitle")}</p>
        </div>
      </div>

      {/* ═══ Eintrag-Formular ═══ */}
      <div className="bg-white rounded-2xl border border-[#2D2D2D]/5 overflow-hidden">
        <div className="px-5 py-4 border-b border-[#2D2D2D]/5">
          <h2 className="text-base font-semibold text-[#2d3a1a]">
            {hasExisting ? t("schlaf.eintragsBearbeiten") : t("schlaf.neuerEintrag")}
          </h2>
          <p className="text-xs text-[#8a9680] mt-0.5">
            {hasExisting
              ? t("schlaf.vorhandenHinweis")
              : t("schlaf.wieGeschlafen")}
          </p>
        </div>

        <div className="p-5 space-y-6">
          {/* Datum-Auswahl */}
          <div>
            <label className="text-xs font-medium text-[#2D2D2D]/60 mb-2 block">{t("schlaf.datum")}</label>
            <input
              type="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              max={getTodayDate()}
              className="w-full px-3 py-2 rounded-xl border border-[#2D2D2D]/10 text-sm text-[#2d3a1a] focus:outline-none focus:ring-2 focus:ring-[#6b7f4e]/30 bg-[#f8f8f8]/50"
            />
          </div>

          {loadingToday ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-[#6b7f4e]" />
            </div>
          ) : (
            <>
              {/* Schlafqualität */}
              <div>
                <label className="text-sm font-medium text-[#2d3a1a] mb-3 block">{t("schlaf.wieGeschlafen")}</label>
                <div className="flex justify-between gap-2">
                  {QUALITY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setQuality(opt.value)}
                      className={`flex flex-col items-center gap-1.5 flex-1 py-3 rounded-xl transition-all ${
                        quality === opt.value
                          ? "bg-[#6b7f4e]/10 ring-2 ring-[#6b7f4e]/40 scale-105"
                          : "hover:bg-[#f8f8f8]"
                      }`}
                    >
                      <span className="text-2xl">{opt.emoji}</span>
                      <span className="text-[10px] text-[#2D2D2D]/50 font-medium">{t(`schlaf.${opt.labelKey}`)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Zeiten */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-xs font-medium text-[#2D2D2D]/60 mb-2 flex items-center gap-1.5">
                    <Moon className="w-3.5 h-3.5" /> {t("schlaf.eingeschlafen")}
                  </label>
                  <input
                    type="time"
                    value={bedtime}
                    onChange={(e) => setBedtime(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border border-[#2D2D2D]/10 text-sm text-[#2d3a1a] focus:outline-none focus:ring-2 focus:ring-[#6b7f4e]/30 bg-[#f8f8f8]/50"
                  />
                </div>
                <div>
                  <label className="text-xs font-medium text-[#2D2D2D]/60 mb-2 flex items-center gap-1.5">
                    <Sun className="w-3.5 h-3.5" /> {t("schlaf.aufgewacht")}
                  </label>
                  <input
                    type="time"
                    value={wakeTime}
                    onChange={(e) => setWakeTime(e.target.value)}
                    className="w-full px-3 py-2.5 rounded-xl border border-[#2D2D2D]/10 text-sm text-[#2d3a1a] focus:outline-none focus:ring-2 focus:ring-[#6b7f4e]/30 bg-[#f8f8f8]/50"
                  />
                </div>
              </div>

              {/* Erholung */}
              <div>
                <label className="text-sm font-medium text-[#2d3a1a] mb-3 block">{t("schlaf.wieErholt")}</label>
                <div className="flex justify-between gap-2">
                  {RECOVERY_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setRecovery(opt.value)}
                      className={`flex flex-col items-center gap-1.5 flex-1 py-3 rounded-xl transition-all ${
                        recovery === opt.value
                          ? "bg-[#6b7f4e]/10 ring-2 ring-[#6b7f4e]/40 scale-105"
                          : "hover:bg-[#f8f8f8]"
                      }`}
                    >
                      <span className="text-2xl">{opt.emoji}</span>
                      <span className="text-[10px] text-[#2D2D2D]/50 font-medium">{t(`schlaf.${opt.labelKey}`)}</span>
                    </button>
                  ))}
                </div>
              </div>

              {/* Einschlafen */}
              <div>
                <label className="text-sm font-medium text-[#2d3a1a] mb-3 block">{t("schlaf.einschlafen")}</label>
                <div className="flex flex-wrap gap-2">
                  {FALL_ASLEEP_OPTIONS.map((opt) => (
                    <button
                      key={opt.value}
                      onClick={() => setFallAsleep(opt.value)}
                      className={`px-4 py-2 rounded-xl text-sm font-medium transition-all ${
                        fallAsleep === opt.value
                          ? "bg-[#6b7f4e] text-white shadow-sm"
                          : "bg-[#f8f8f8] text-[#2D2D2D]/60 hover:bg-[#6b7f4e]/10"
                      }`}
                    >
                      {t(`schlaf.${opt.labelKey}`)}
                    </button>
                  ))}
                </div>
              </div>

              {/* Nachts aufgewacht + Träume */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="text-sm font-medium text-[#2d3a1a] mb-3 block">{t("schlaf.nachtsAufgewacht")}</label>
                  <div className="flex items-center gap-3">
                    <button
                      onClick={() => setNightWakeups(Math.max(0, nightWakeups - 1))}
                      className="w-10 h-10 rounded-xl bg-[#f8f8f8] flex items-center justify-center text-[#2D2D2D]/40 hover:bg-[#6b7f4e]/10 transition-colors"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <span className="text-2xl font-bold text-[#2d3a1a] w-8 text-center">{nightWakeups}</span>
                    <button
                      onClick={() => setNightWakeups(nightWakeups + 1)}
                      className="w-10 h-10 rounded-xl bg-[#f8f8f8] flex items-center justify-center text-[#2D2D2D]/40 hover:bg-[#6b7f4e]/10 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                </div>
                <div>
                  <label className="text-sm font-medium text-[#2d3a1a] mb-3 block">&nbsp;</label>
                  <button
                    onClick={() => setDreamsRemembered(!dreamsRemembered)}
                    className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-medium transition-all ${
                      dreamsRemembered
                        ? "bg-[#6b7f4e]/10 text-[#6b7f4e] ring-2 ring-[#6b7f4e]/30"
                        : "bg-[#f8f8f8] text-[#2D2D2D]/50 hover:bg-[#6b7f4e]/5"
                    }`}
                  >
                    <Sparkles className="w-4 h-4" />
                    {t("schlaf.traeumeErinnert")}
                  </button>
                </div>
              </div>

              {/* Notizen */}
              <div>
                <label className="text-sm font-medium text-[#2d3a1a] mb-2 block">{t("schlaf.notizen")}</label>
                <textarea
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  placeholder={t("schlaf.notizenPlaceholder")}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl border border-[#2D2D2D]/10 text-sm text-[#2d3a1a] placeholder:text-[#2D2D2D]/25 focus:outline-none focus:ring-2 focus:ring-[#6b7f4e]/30 bg-[#f8f8f8]/50 resize-none"
                />
              </div>

              {/* Speichern-Button */}
              <button
                onClick={handleSave}
                disabled={upsertMutation.isPending}
                className="w-full py-3 rounded-xl bg-[#6b7f4e] text-white font-semibold text-sm hover:bg-[#5a6b3e] transition-colors shadow-sm disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {upsertMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : null}
                {hasExisting ? t("schlaf.aktualisieren") : t("schlaf.speichern")}
              </button>
            </>
          )}
        </div>
      </div>

      {/* ═══ Statistiken (letzte 14 Tage) ═══ */}
      {stats && (
        <div className="bg-white rounded-2xl border border-[#2D2D2D]/5 overflow-hidden">
          <div className="px-5 py-4 border-b border-[#2D2D2D]/5 flex items-center gap-2">
            <BarChart3 className="w-4 h-4 text-[#6b7f4e]" />
            <h2 className="text-sm font-semibold text-[#2d3a1a]">{t("schlaf.letzte14")}</h2>
          </div>
          <div className="p-5">
            {/* Ø Qualität + Ø Schlafdauer */}
            <div className="grid grid-cols-3 gap-2 mb-6 text-center">
              <div className="text-center">
                <p className="text-2xl font-bold text-[#2d3a1a]">{stats.avgQuality.toFixed(1)}</p>
                <p className="text-xs text-[#8a9680]">{t("schlaf.avQualitaet")}</p>
                <div className="flex justify-center mt-1">
                  {[1, 2, 3, 4, 5].map((s) => (
                    <Star
                      key={s}
                      className={`w-3 h-3 ${s <= Math.round(stats.avgQuality) ? "text-amber-400 fill-amber-400" : "text-[#2D2D2D]/10"}`}
                    />
                  ))}
                </div>
              </div>
              <div className="text-center">
                <p className="text-2xl font-bold text-[#2d3a1a]">{stats.avgDurationStr}</p>
                <p className="text-xs text-[#8a9680]">{t("schlaf.avSchlafdauer")}</p>
              </div>
              <div className="text-center">
                {stats.count >= 3 ? (
                  <>
                    <p className="text-2xl font-bold text-[#2d3a1a]">{stats.count}</p>
                    <p className="text-xs text-[#8a9680]">{t("schlaf.eintraege")}</p>
                  </>
                ) : (
                  <>
                    <p className="text-lg font-medium text-[#2D2D2D]/30">—</p>
                    <p className="text-xs text-[#8a9680]">{t("schlaf.zuWenigDaten")}</p>
                  </>
                )}
              </div>
            </div>

            {/* Qualitätsverlauf (einfacher Balken-Chart) */}
            <div className="mb-6">
              <p className="text-xs font-medium text-[#8a9680] mb-3">{t("schlaf.qualitaetsverlauf")}</p>
              <div className="flex items-end gap-1 h-20">
                {(recentEntries ?? [])
                  .slice()
                  .reverse()
                  .map((entry, i) => {
                    const qOpt = QUALITY_OPTIONS[entry.quality - 1];
                    return (
                    <div
                      key={entry.id}
                      className="flex-1 rounded-t-sm transition-all"
                      style={{
                        height: `${(entry.quality / 5) * 100}%`,
                        backgroundColor:
                          entry.quality >= 4
                            ? "#6b7f4e"
                            : entry.quality === 3
                            ? "#8a9680"
                            : "#d97706",
                        opacity: 0.6 + (i / ((recentEntries?.length ?? 1) - 1 || 1)) * 0.4,
                      }}
                      title={`${formatDateDisplay(entry.date, t("common.today"), t("common.yesterday"))}: ${qOpt ? t(`schlaf.${qOpt.labelKey}`) : ""}`}
                    />
                    );
                  })}
              </div>
            </div>

            {/* Beste & Schlechteste Nacht */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="bg-[#6b7f4e]/5 rounded-xl p-3">
                <p className="text-xs font-semibold text-[#6b7f4e] mb-1">{t("schlaf.besteNacht")}</p>
                <p className="text-xs text-[#2D2D2D]/60">{formatDateDisplay(stats.best.date, t("common.today"), t("common.yesterday"))}</p>
                <span className="text-lg">{QUALITY_OPTIONS[stats.best.quality - 1]?.emoji}</span>
              </div>
              <div className="bg-amber-50 rounded-xl p-3">
                <p className="text-xs font-semibold text-amber-700 mb-1">{t("schlaf.schlechtesteNacht")}</p>
                <p className="text-xs text-[#2D2D2D]/60">{formatDateDisplay(stats.worst.date, t("common.today"), t("common.yesterday"))}</p>
                <span className="text-lg">{QUALITY_OPTIONS[stats.worst.quality - 1]?.emoji}</span>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ═══ Info-Banner: Schlaf fließt ins Coaching ein ═══ */}
      <div className="bg-[#6b7f4e]/5 rounded-2xl border border-[#6b7f4e]/10 p-4 flex items-start gap-3">
        <div className="w-8 h-8 rounded-full bg-[#6b7f4e]/10 flex items-center justify-center flex-shrink-0 mt-0.5">
          <Info className="w-4 h-4 text-[#6b7f4e]" />
        </div>
        <div>
          <p className="text-sm font-semibold text-[#2d3a1a]">{t("schlaf.coachingEinfliessen")}</p>
          <p className="text-xs text-[#8a9680] mt-0.5">
            {t("schlaf.coachingEinfliessenDesc")}
          </p>
        </div>
      </div>

      {/* ═══ Wearable-Integration Hinweis ═══ */}
      <div className="bg-white rounded-2xl border border-dashed border-[#2D2D2D]/10 overflow-hidden">
        <div className="p-6 text-center space-y-3">
          <div className="w-12 h-12 rounded-full bg-[#6b7f4e]/5 flex items-center justify-center mx-auto">
            <Watch className="w-6 h-6 text-[#6b7f4e]/40" />
          </div>
          <h3 className="text-sm font-semibold text-[#2d3a1a]/70" style={{ fontFamily: "'Playfair Display', serif" }}>
            {t("schlaf.wearableIntegration")}
          </h3>
          <p className="text-xs text-[#8a9680] max-w-sm mx-auto leading-relaxed">
            {t("schlaf.wearableDesc")}
          </p>
          <div className="flex flex-wrap justify-center gap-3 text-[11px] text-[#2D2D2D]/40 font-medium">
            <span className="flex items-center gap-1"><Smartphone className="w-3 h-3" /> Apple Watch</span>
            <span className="flex items-center gap-1"><Watch className="w-3 h-3" /> Oura Ring</span>
            <span className="flex items-center gap-1"><Watch className="w-3 h-3" /> Whoop</span>
            <span className="flex items-center gap-1"><Watch className="w-3 h-3" /> Fitbit</span>
            <span className="flex items-center gap-1"><Watch className="w-3 h-3" /> Garmin</span>
          </div>
          <a
            href="/wearables"
            className="inline-flex items-center gap-1.5 text-xs font-medium text-[#C8A55A] hover:text-[#b8953a] transition-colors mt-1"
          >
            <Zap className="w-3.5 h-3.5" />
            {t("schlaf.demnächst")}
          </a>
        </div>
      </div>

      {/* ═══ Letzte Einträge ═══ */}
      {recentEntries && recentEntries.length > 0 && (
        <div className="bg-white rounded-2xl border border-[#2D2D2D]/5 overflow-hidden">
          <div className="px-5 py-4 border-b border-[#2D2D2D]/5 flex items-center gap-2">
            <Calendar className="w-4 h-4 text-[#6b7f4e]" />
            <h2 className="text-sm font-semibold text-[#2d3a1a]">{t("schlaf.letzteEintraege")}</h2>
          </div>
          <div className="divide-y divide-[#2D2D2D]/5">
            {recentEntries.map((entry) => (
              <div
                key={entry.id}
                className="px-5 py-4 flex items-center gap-4 hover:bg-[#f8f8f8]/50 transition-colors cursor-pointer"
                onClick={() => setSelectedDate(entry.date)}
              >
                <span className="text-2xl">{QUALITY_OPTIONS[entry.quality - 1]?.emoji}</span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#2d3a1a]">{formatDateDisplay(entry.date, t("common.today"), t("common.yesterday"))}</p>
                  <p className="text-xs text-[#8a9680] mt-0.5 flex items-center gap-2">
                    <Clock className="w-3 h-3 inline" />
                    {entry.bedtime} – {entry.wakeTime}
                    <span className="text-[#6b7f4e] font-medium">{calcSleepDuration(entry.bedtime, entry.wakeTime)}</span>
                  </p>
                </div>
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDelete(entry.id);
                  }}
                  className="p-2 rounded-lg text-[#2D2D2D]/20 hover:text-red-500 hover:bg-red-50 transition-colors"
                  title={t("schlaf.eintraegLoeschen")}
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
